/**
 * geo.ts — AK-320 «Линейка на карте» v1.
 *
 * Геодезические расчёты + форматтеры единиц для measure-инструмента.
 * Все вычисления — через точечные turf-пакеты (НЕ монолит @turf/turf):
 *   - distance  (две точки, км → переводим внутри)
 *   - length    (длина ломаной по сегментам)
 *   - area      (площадь полигона, м²; геодезически по сфероиду)
 *   - circle    (полигон-аппроксимация круга для геодезической площади)
 *
 * Единицы (founder lock — гектары первой):
 *   - площадь → га крупно (1 знак, 2 знака при <10 га) + мелким м²(<1 га)/км²(>100 га)
 *   - расстояние → м (<1000) / км (≥1000)
 *
 * Чистые функции — без зависимости от MapLibre / DOM, удобно тестировать.
 */
import distance from '@turf/distance';
import length from '@turf/length';
import area from '@turf/area';
import circle from '@turf/circle';
import { lineString, polygon, point } from '@turf/helpers';

/** [lng, lat] — порядок MapLibre / GeoJSON. */
export type LngLat = [number, number];

// ─────────────────────────────────────────────────────────────────────────
// Геодезия
// ─────────────────────────────────────────────────────────────────────────

/** Расстояние между двумя точками, метры. */
export function distanceMeters(a: LngLat, b: LngLat): number {
  return distance(point(a), point(b), { units: 'meters' });
}

/**
 * Суммарная длина ломаной по N точкам, метры.
 * <2 точек → 0.
 */
export function pathLengthMeters(coords: LngLat[]): number {
  if (coords.length < 2) return 0;
  return length(lineString(coords), { units: 'meters' });
}

/**
 * Длины каждого сегмента ломаной, метры. Возвращает массив длиной
 * `coords.length - 1` (пустой при <2 точках).
 */
export function segmentLengthsMeters(coords: LngLat[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < coords.length; i++) {
    out.push(distanceMeters(coords[i - 1], coords[i]));
  }
  return out;
}

/**
 * Геодезическая площадь замкнутого полигона, м². Принимает кольцо точек
 * (без обязательного дублирования первой точки в конце — замыкаем сами).
 * <3 точек → 0.
 */
export function polygonAreaSqm(coords: LngLat[]): number {
  if (coords.length < 3) return 0;
  const ring = closeRing(coords);
  return area(polygon([ring]));
}

/**
 * Периметр замкнутого полигона, метры (включая замыкающий сегмент
 * последняя→первая). <2 точек → 0.
 */
export function polygonPerimeterMeters(coords: LngLat[]): number {
  if (coords.length < 2) return 0;
  return pathLengthMeters(closeRing(coords));
}

/**
 * Полигон-аппроксимация круга (geodesic) для отрисовки. center + radius(м).
 * steps=64 — гладкая окружность без видимых граней на типовых зумах.
 * Возвращает кольцо [lng,lat][] (первая точка == последняя).
 */
export function circlePolygon(center: LngLat, radiusMeters: number, steps = 64): LngLat[] {
  if (radiusMeters <= 0) return [];
  const f = circle(point(center), radiusMeters, { steps, units: 'meters' });
  // circle() возвращает Feature<Polygon> с единственным внешним кольцом.
  return f.geometry.coordinates[0] as LngLat[];
}

/**
 * Геодезическая площадь круга, м². Считаем через площадь полигон-
 * аппроксимации (НЕ плоская π·r²) — корректно учитывает проекцию.
 */
export function circleAreaSqm(center: LngLat, radiusMeters: number, steps = 64): number {
  if (radiusMeters <= 0) return 0;
  const ring = circlePolygon(center, radiusMeters, steps);
  if (ring.length < 4) return 0;
  return area(polygon([ring]));
}

/** Замыкает кольцо: добавляет первую точку в конец, если её там нет. */
function closeRing(coords: LngLat[]): LngLat[] {
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return coords;
  return [...coords, first];
}

// ─────────────────────────────────────────────────────────────────────────
// Форматтеры
// ─────────────────────────────────────────────────────────────────────────

/**
 * Форматирует число: точка — десятичный разделитель, узкий пробел (U+202F) —
 * разделитель тысяч в целой части. Хвостовые нули в дробной части уже убраны
 * округлением (roundTo) до вызова. Моно-цифры в UI → читаемо и стабильно.
 */
const THIN_SPACE = '\u202f';
function fmtNum(n: number): string {
  const [intPart, fracPart] = String(n).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, THIN_SPACE);
  return fracPart ? `${grouped}.${fracPart}` : grouped;
}

/**
 * Расстояние/длина: м (<1000) / км (≥1000).
 *   850   → «850 м»
 *   1500  → «1.5 км»
 *   12300 → «12.3 км»
 */
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '0 м';
  if (meters < 1000) {
    return `${fmtNum(Math.round(meters))} м`;
  }
  const km = meters / 1000;
  // <10 км → 2 знака, иначе 1 знак (не зашумляем большие значения).
  const digits = km < 10 ? 2 : 1;
  return `${fmtNum(roundTo(km, digits))} км`;
}

/**
 * Площадь (founder lock — гектары первой):
 *   - основное значение всегда в га:
 *       <10 га   → 2 знака
 *       ≥10 га   → 1 знак
 *   - вторичная (мелкая) единица для контекста:
 *       <1 га    → м²
 *       >100 га  → км²
 * Возвращает { primary, secondary? } чтобы UI отрисовал га крупно, контекст мелко.
 */
export interface AreaParts {
  /** Основное значение, гектары (строка с единицей). */
  primary: string;
  /** Контекстная единица (м² / км²), либо undefined в среднем диапазоне. */
  secondary?: string;
}

export function formatArea(sqm: number): AreaParts {
  if (!Number.isFinite(sqm) || sqm <= 0) return { primary: '0 га' };
  const ha = sqm / 10_000;
  const haDigits = ha < 10 ? 2 : 1;
  const primary = `${fmtNum(roundTo(ha, haDigits))} га`;

  let secondary: string | undefined;
  if (ha < 1) {
    // <1 га — добавим м² (целое) как привычный мелкий ориентир.
    secondary = `${fmtNum(Math.round(sqm))} м²`;
  } else if (ha > 100) {
    // >100 га — добавим км² (2 знака).
    secondary = `${fmtNum(roundTo(sqm / 1_000_000, 2))} км²`;
  }
  return { primary, secondary };
}

/** Площадь одной строкой (для подписи-оверлея на карте). */
export function formatAreaInline(sqm: number): string {
  const { primary, secondary } = formatArea(sqm);
  return secondary ? `${primary} (${secondary})` : primary;
}

/** Округление до N знаков без хвостовых нулей в строковом представлении. */
function roundTo(n: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
}
